import { useCallback, useEffect, useState } from 'react'
import type {
  EnvoiDTO, OptionsBesogneDTO, PenchantBesogneDTO, RegionBesogneDTO,
} from '@whatremains/shared'
import { api, ErreurApi } from '../api'
import { IconeObjet } from '../composants/IconeObjet'
import { TypeObjet } from '../composants/TypeObjet'

/**
 * Les Besognes.
 *
 * On assigne, on ferme, on revient. L'écran montre **ce que la zone donne en
 * nature** (son profil) et **ce qu'on risque** (la chance d'incident), jamais
 * ce qu'on va gagner en chiffres : le résultat est scellé au départ, et le dire
 * d'avance supprimerait l'attente.
 */

const NOMS_PROFIL: [keyof RegionBesogneDTO['profil'], string][] = [
  ['or', 'or'],
  ['xp', 'expérience'],
  ['materiaux', 'matériaux'],
  ['equipement', 'équipement'],
]

const ISSUES: Record<NonNullable<EnvoiDTO['issue']>, string> = {
  accompli: 'Rentré',
  blesse: 'Rentré blessé',
  allege: 'Rentré allégé',
}

function Jauge({ n }: { n: number }) {
  return (
    <span style={{ fontFamily: 'var(--mono)', letterSpacing: '.1em' }}>
      {'▮'.repeat(Math.max(0, Math.min(8, n)))}
      <span className="tenu">{'▯'.repeat(Math.max(0, 8 - n))}</span>
    </span>
  )
}

function EnCours({ e, surRelever }: { e: EnvoiDTO; surRelever: (id: string) => void }) {
  return (
    <div className="paire" style={{ gridTemplateColumns: '1fr auto' }}>
      <span>
        <span className={e.prete ? '' : 'attenue'}>{e.besogneNom}</span>
        <span className="tenu" style={{ marginLeft: 12, fontFamily: 'var(--mono)', fontSize: 10.5 }}>
          {e.regionNom}
        </span>
      </span>
      <span style={{ whiteSpace: 'nowrap' }}>
        {e.prete ? (
          <button className="action" onClick={() => surRelever(e.id)}>Relever</button>
        ) : (
          <span className="tenu" style={{ fontFamily: 'var(--mono)', fontSize: 11 }}>
            rentre dans {e.minutesRestantes} min
          </span>
        )}
      </span>
    </div>
  )
}

function Releve({ e }: { e: EnvoiDTO }) {
  return (
    <div className="paire" style={{ gridTemplateColumns: '150px 1fr' }}>
      <b>{e.issue ? ISSUES[e.issue] : ''}</b>
      <span style={{ fontFamily: 'var(--mono)', fontSize: 11.5 }}>
        {e.besogneNom}, {e.regionNom}
        {e.partAccomplie < 100 && (
          <span style={{ color: 'var(--r6)', marginLeft: 12 }}>{e.partAccomplie} % accompli</span>
        )}
        <span style={{ marginLeft: 14 }}>{e.or} or</span>
        <span style={{ marginLeft: 12 }}>{e.experience} xp</span>
        {e.renom.map((r) => (
          <span key={r.code} style={{ marginLeft: 12 }}>
            {r.nom} +{r.valeur}
          </span>
        ))}
        {e.objets.map((piece, i) => (
          <span key={`${piece.code}-${i}`} className="sac-piece" style={{ marginLeft: 12 }}>
            <IconeObjet objet={piece} taille={20} />
            <span style={{ color: 'var(--r2)' }}>
              {piece.nom}
              <TypeObjet o={piece} className="objet-type objet-type-ligne" />
            </span>
          </span>
        ))}
        {e.trouvailleNom && (
          <span style={{ marginLeft: 12, color: 'var(--r7)' }}>trouvé : {e.trouvailleNom}</span>
        )}
        {/*
          La matière rapportée. Vert comme partout ailleurs pour ce qui se
          consomme et se transforme, et dite ici plutôt que découverte dans les
          sacs : une source qu'on ne voit pas ne répond pas au problème qu'elle
          règle.
        */}
        {e.matiere && (
          <span style={{ marginLeft: 12, color: 'var(--r2)' }}>
            {e.matiere.nombre} {e.matiere.nom.toLowerCase()}
          </span>
        )}
      </span>
    </div>
  )
}

/**
 * Vers quoi une besogne penche, comparée aux autres de sa région.
 *
 * Enzo, sur un premier jet qui donnait les montants exacts : « c'est trop
 * précis. Je voulais juste savoir en gros si je vais avoir plus ou moins d'XP
 * que les autres, plus ou moins d'or, plus ou moins d'équipement. Mais pas
 * forcément les chiffres précis, sans non plus que ce soit complètement
 * scripté de mon point de vue. »
 *
 * Il redit la doctrine écrite en tête de ce fichier, que j'avais enfreinte :
 * l'écran montre ce qu'on obtient EN NATURE, jamais en chiffres. Un montant
 * exact ne fait pas qu'informer, il désigne la bonne réponse, et le choix
 * cesse d'en être un.
 *
 * Même jauge que le profil d'une région, affiché juste au-dessus : une seule
 * unité à apprendre pour tout l'écran. Un axe où TOUTES les besognes de la
 * région sont à zéro ne s'affiche pas — une colonne de jauges vides n'apprend
 * rien et pousse le reste vers le bas.
 */
const AXES_PENCHANT: [keyof PenchantBesogneDTO, string][] = [
  ['or', 'or'],
  ['xp', 'expérience'],
  ['equipement', 'équipement'],
  ['trouvaille', 'trouvaille'],
  ['renom', 'renom'],
]

function Penchant({
  penchant,
  voisines,
}: {
  penchant: PenchantBesogneDTO
  /** Les autres besognes de la région, pour savoir quel axe vaut la peine. */
  voisines: PenchantBesogneDTO[]
}) {
  const utiles = AXES_PENCHANT.filter(([k]) => voisines.some((v) => v[k] > 0))
  return (
    <p style={{ marginTop: 10, fontFamily: 'var(--mono)', fontSize: 10.5, lineHeight: 1.9 }}>
      {utiles.map(([k, nom]) => (
        <span key={String(k)} style={{ display: 'block' }}>
          <span className="tenu" style={{ display: 'inline-block', width: 82 }}>{nom}</span>
          <Jauge n={penchant[k]} />
        </span>
      ))}
    </p>
  )
}

export function Besognes({
  personnageId,
  surChangement,
}: {
  personnageId: string
  surChangement: () => void
}) {
  const [options, setOptions] = useState<OptionsBesogneDTO | null>(null)
  const [envois, setEnvois] = useState<EnvoiDTO[]>([])
  const [region, setRegion] = useState<string | null>(null)
  const [besogne, setBesogne] = useState<string | null>(null)
  const [duree, setDuree] = useState<number>(120)
  const [erreur, setErreur] = useState<string | null>(null)
  const [toutMontrer, setToutMontrer] = useState(false)
  const [note, setNote] = useState<string | null>(null)

  const recharger = useCallback(async () => {
    const [o, e] = await Promise.all([api.besognes(personnageId), api.envois(personnageId)])
    setOptions(o)
    setEnvois(e)
  }, [personnageId])

  useEffect(() => { void recharger() }, [recharger])

  // Le compte à rebours vient du serveur : on le relit, on ne le décrémente pas.
  useEffect(() => {
    const t = setInterval(() => { void recharger() }, 30_000)
    return () => clearInterval(t)
  }, [recharger])

  if (!options) return <div className="cadre"><div className="entete"><p className="tenu">…</p></div></div>

  const choisie = options.regions.find((r) => r.code === region) ?? null
  /**
   * Ce qu'on montre, et ce qu'on replie.
   *
   * `ecart` vaut `seuil de région - niveau du joueur` : il est donc NÉGATIF
   * quand la région est sous lui, et c'est ce signe qui sert de mesure. À -10,
   * la région ne présente plus ni danger ni rendement notable.
   */
  const perimee = (r: RegionBesogneDTO) =>
    r.connue && r.ecart <= -10 && r.lieuxAdecouvrir === 0
  const toutes = options?.regions ?? []
  const repliees = toutes.filter(perimee)
  const visibles = toutMontrer ? toutes : toutes.filter((r) => !perimee(r))

  const enCours = envois.filter((e) => !e.releve)
  const releves = envois.filter((e) => e.releve).slice(0, 8)
  const complet = options.enCours >= options.capacite

  async function agir(action: () => Promise<unknown>, message?: string) {
    setErreur(null)
    setNote(null)
    try {
      await action()
      if (message) setNote(message)
      await recharger()
      surChangement()
    } catch (e) {
      setErreur(e instanceof ErreurApi ? e.message : "Quelque chose n'a pas abouti.")
    }
  }

  return (
    <div className="cadre">
      <div className="entete">
        <div className="surtitre">
          {options.enCours} sur {options.capacite} en route
        </div>
        <h1>Besognes</h1>
        <p className="attenue" style={{ maxWidth: '54ch' }}>
          Tu envoies, tu pars, tu reviens. Ce qui se passe pendant se décide au départ.
        </p>
        {note && <p className="attenue">{note}</p>}
        {erreur && <p className="erreur">{erreur}</p>}
      </div>

      {enCours.length > 0 && (
        <section>
          <div className="surtitre">En route</div>
          <div className="paires">
            {enCours.map((e) => (
              <EnCours
                key={e.id}
                e={e}
                surRelever={(id) => void agir(() => api.relever(personnageId, id))}
              />
            ))}
          </div>
        </section>
      )}

      <section>
        <div className="surtitre">Où</div>
        {complet && (
          <p className="tenu" style={{ marginBottom: 14 }}>
            {options.capacite === 1
              ? 'Tu ne peux être qu’à un endroit.'
              : `Tu en as déjà ${options.capacite} en route.`}
          </p>
        )}
        {/*
          Les régions qu'on ne rouvrira pas se replient.

          Enzo : « je ne vais jamais me retaper les besognes de niveau 1, de
          niveau 4, de niveau 8. Si j'ai déjà tout découvert, les garder sur
          l'écran, ça peut ne pas être très utile. »

          Deux conditions, et il faut les DEUX. Le seuil loin derrière soi ne
          suffit pas : une région basse où il reste des lieux à trouver garde une
          raison d'y envoyer quelqu'un, et c'est même le seul moyen de la
          fouiller sans s'y rendre. Inversement, tout découvrir dans une région
          de son propre niveau ne la périme pas.

          Replier et non SUPPRIMER : l'écart de dix niveaux est un jugement, pas
          une vérité, et le jour où il se trompe on veut pouvoir rouvrir d'un
          clic plutôt que découvrir qu'une région a disparu.
        */}
        <div className="grille">
          {visibles.map((r) => (
            <button
              key={r.code}
              className="choix"
              aria-pressed={region === r.code}
              disabled={complet || !r.connue}
              style={{ opacity: complet || !r.connue ? 0.4 : 1 }}
              onClick={() => { setRegion(r.code); setBesogne(null) }}
            >
              <span className="role">
                niveau {r.seuilNiveau}
                {r.ecart > 0 && <span style={{ color: 'var(--r6)' }}> · au-dessus de toi</span>}
                {/*
                  Verrouillée, et elle dit pourquoi.

                  Enzo voulait tout piloter depuis cet écran sans jamais avoir à
                  se déplacer pour AGIR, mais acceptait de déverrouiller « en
                  fonction de là où je suis déjà allé ». La région reste donc
                  visible : on voit ce qu'on rate et ce qu'il faut faire pour
                  l'avoir. La cacher aurait supprimé la seule chose qui rend la
                  contrainte apprenable.
                */}
                {!r.connue && (
                  <span style={{ color: 'var(--r6)' }}> · tu n’y es jamais allé</span>
                )}
              </span>
              <span className="nom">{r.nom}</span>
              <p style={{ marginTop: 10, fontFamily: 'var(--mono)', fontSize: 10.5, lineHeight: 1.9 }}>
                {NOMS_PROFIL.map(([k, nom]) => (
                  <span key={k} style={{ display: 'block' }}>
                    <span className="tenu" style={{ display: 'inline-block', width: 82 }}>{nom}</span>
                    <Jauge n={r.profil[k] ?? 0} />
                  </span>
                ))}
              </p>
              <p style={{ marginTop: 10, fontFamily: 'var(--mono)', fontSize: 10.5 }}>
                <span style={{ color: r.chanceIncident > 20 ? 'var(--r6)' : 'var(--ink-dim)' }}>
                  incident {r.chanceIncident} %
                </span>
                {r.lieuxAdecouvrir > 0 && (
                  <span className="tenu" style={{ marginLeft: 14 }}>
                    {r.lieuxAdecouvrir} à découvrir
                  </span>
                )}
              </p>
            </button>
          ))}
        </div>
        {repliees.length > 0 && (
          <p style={{ marginTop: 12 }}>
            <button className="discret" onClick={() => setToutMontrer((v) => !v)}>
              {toutMontrer
                ? `replier les ${repliees.length} régions épuisées`
                : `${repliees.length} région${repliees.length > 1 ? 's' : ''} épuisée${repliees.length > 1 ? 's' : ''} · tout afficher`}
            </button>
          </p>
        )}
      </section>

      {choisie && !complet && (
        <section>
          <div className="surtitre">Quoi</div>
          <div className="grille">
            {choisie.besognes.map((b) => (
              <button
                key={b.code}
                className="choix"
                aria-pressed={besogne === b.code}
                onClick={() => setBesogne(b.code)}
              >
                <span className="nom">{b.nom}</span>
                <p style={{ marginTop: 8 }}>{b.texte}</p>
                {/*
                  Vers quoi elle penche. Sans ce bloc, la seule différence
                  visible entre les besognes d'une région était leur nom.
                */}
                <Penchant
                  penchant={b.penchant}
                  voisines={choisie.besognes.map((x) => x.penchant)}
                />
                {b.statFavorisee && (
                  <p className="tenu" style={{ marginTop: 8, fontFamily: 'var(--mono)', fontSize: 10 }}>
                    penche {b.statFavorisee}
                  </p>
                )}
              </button>
            ))}
          </div>
          {/*
            Dire que cette jauge-ci est RELATIVE, parce que celle du dessus ne
            l'est pas.

            Enzo, sur deux jauges d'apparence identique : « est-ce que ces
            deuxièmes jauges surpassent les premières et donc les premières ne
            servent à rien ? […] Est-ce que c'est complètement décorrélé et dans
            ce cas-là c'est bugé ? »

            Rien n'est décorrélé — le rendement multiplie le profil de la région
            par celui de la besogne — mais les deux jauges ne se lisent pas dans
            la même unité, et rien ne le disait. Celle de la région est absolue,
            celle-ci situe la besogne PARMI CELLES D'ICI. Une besogne au maximum
            dans une région pauvre rend donc moins qu'une besogne médiocre dans
            une région riche, ce qu'on ne peut pas deviner de deux barres qui se
            ressemblent.
          */}
          <p className="tenu" style={{ fontSize: 12, maxWidth: '58ch', marginTop: 14 }}>
            Ces barres comparent les besognes <b>entre elles, ici</b>. Ce que tu
            gagnes vraiment multiplie les deux : le profil de la région d’abord,
            le penchant de la besogne ensuite. Une région riche reste riche même
            avec la besogne qui y rend le moins.
          </p>
        </section>
      )}

      {choisie && besogne && !complet && (
        <section>
          <div className="surtitre">Combien de temps</div>
          <div className="grille" style={{ maxWidth: 780 }}>
            {options.durees.map((d) => (
              <button
                key={d.minutes}
                className="choix"
                aria-pressed={duree === d.minutes}
                onClick={() => setDuree(d.minutes)}
              >
                <span className="nom">{d.nom}</span>
              </button>
            ))}
          </div>
          {/*
            La phrase disait exactement l'inverse du code.

            « Plus c'est long, plus ça rend à l'heure » : les facteurs sont 1,4
            pour trente minutes, 1,32 pour deux heures et 1,25 pour huit. C'est
            donc le COURT qui rend le plus à l'heure, et le commentaire du
            domaine le dit noir sur blanc — « 4 × 30 minutes vaut 6,1 % de plus
            qu'une de 2 heures ». Un joueur qui lisait cette ligne choisissait
            l'inverse de ce qu'il croyait choisir.
          */}
          <p className="tenu" style={{ fontSize: 12, maxWidth: '52ch', marginTop: 14 }}>
            Plus c’est court, plus ça rend à l’heure, à condition de revenir le relever.
            Le long rapporte plus d’un coup, et te laisse plus longtemps sans pouvoir réagir.
          </p>
          <p style={{ marginTop: 18 }}>
            <button
              className="action"
              onClick={() =>
                void agir(
                  () => api.envoyer(personnageId, choisie.code, besogne, duree),
                  'Parti.',
                )
              }
            >
              Envoyer
            </button>
          </p>
        </section>
      )}

      {releves.length > 0 && (
        <section>
          <div className="surtitre">Rentrés</div>
          <div className="paires">
            {releves.map((e) => <Releve key={e.id} e={e} />)}
          </div>
        </section>
      )}
    </div>
  )
}
